<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue';
import { RouterLink, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/auth';
import { useFeatureFlag } from '@/composables/useFeatureFlag';

// Marketing-site URLs. DNS has cut over to the new etaiota-site — these were
// left pointing at the old legacy paths (HC.shtml, 1855club/1855club.shtml,
// etc.) from before that happened and now 404. Updated to the new site's
// clean paths, verified live (2026-08-04).
const MARKETING_HOME = 'https://www.etaiota.org';
const HOUSE_CORP_URL = 'https://www.etaiota.org/house-corp/';
const TENANT_RESOURCES_URL = 'https://www.etaiota.org/house-corp/tenant-resources/';
const NEWS_URL = 'https://www.etaiota.org/news/';
const CLUB_1855_URL = 'https://www.etaiota.org/1855-club/';

const router = useRouter();
const authStore = useAuthStore();
const familyTreeEnabled = useFeatureFlag('family-tree');

const isMobileMenuOpen = ref(false);
const mobileHouseCorpOpen = ref(false);
const mobileMemberDirOpen = ref(false);

// Only one desktop dropdown open at a time.
type DesktopMenu = 'house-corp' | 'member-directory' | 'account' | null;
const openMenu = ref<DesktopMenu>(null);
const navRoot = ref<HTMLElement | null>(null);

function toggleMenu(menu: DesktopMenu) {
  openMenu.value = openMenu.value === menu ? null : menu;
}

function handleOutsideClick(event: MouseEvent) {
  if (navRoot.value && !navRoot.value.contains(event.target as Node)) {
    openMenu.value = null;
  }
}

onMounted(() => {
  document.addEventListener('click', handleOutsideClick);
});

onUnmounted(() => {
  document.removeEventListener('click', handleOutsideClick);
});

async function handleLogout() {
  openMenu.value = null;
  isMobileMenuOpen.value = false;
  await authStore.logout();
  router.push('/login');
}

function closeMobileMenu() {
  isMobileMenuOpen.value = false;
  mobileHouseCorpOpen.value = false;
  mobileMemberDirOpen.value = false;
}
</script>

<template>
  <nav ref="navRoot" class="bg-sigmachi-blue shadow-lg relative z-30">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16 gap-2">
        <!-- Logo -->
        <a :href="MARKETING_HOME" class="flex-shrink-0 flex items-center">
          <img src="/logo.png" alt="Eta Iota" class="h-12 w-auto" />
        </a>

        <!-- Desktop primary nav -->
        <div
          class="hidden min-[860px]:flex min-[860px]:items-center min-[860px]:gap-1 flex-1"
          v-if="authStore.isAuthenticated"
        >
          <a
            :href="MARKETING_HOME"
            class="text-white hover:bg-blue-700 px-3 py-2 rounded-md text-sm font-medium"
          >
            Home
          </a>

          <!-- House Corp dropdown -->
          <div class="relative">
            <button
              type="button"
              class="text-white hover:bg-blue-700 px-3 py-2 rounded-md text-sm font-medium inline-flex items-center gap-1"
              :class="{ 'bg-blue-700': openMenu === 'house-corp' }"
              :aria-expanded="openMenu === 'house-corp'"
              @click.stop="toggleMenu('house-corp')"
            >
              House Corp
              <svg
                class="h-2.5 w-2.5 transition-transform"
                :class="{ 'rotate-180': openMenu === 'house-corp' }"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
            <div
              v-if="openMenu === 'house-corp'"
              class="absolute top-full left-0 mt-1 min-w-[12rem] bg-white border border-gray-200 rounded-lg shadow-lg p-1.5 z-20"
            >
              <a
                :href="HOUSE_CORP_URL"
                class="block px-2.5 py-2 rounded-md text-sm font-medium text-sigmachi-navy hover:bg-gray-100"
              >
                House Corp Overview
              </a>
              <a
                :href="TENANT_RESOURCES_URL"
                class="block px-2.5 py-2 rounded-md text-sm font-medium text-sigmachi-navy hover:bg-gray-100"
              >
                Tenant Resources
              </a>
            </div>
          </div>

          <a
            :href="NEWS_URL"
            class="text-white hover:bg-blue-700 px-3 py-2 rounded-md text-sm font-medium"
          >
            News
          </a>

          <a
            :href="CLUB_1855_URL"
            class="text-white hover:bg-blue-700 px-3 py-2 rounded-md text-sm font-medium"
          >
            1855 Club
          </a>

          <!-- Member Directory dropdown -->
          <div class="relative">
            <button
              type="button"
              class="text-white hover:bg-blue-700 px-3 py-2 rounded-md text-sm font-medium inline-flex items-center gap-1"
              :class="{ 'bg-blue-700': openMenu === 'member-directory' }"
              :aria-expanded="openMenu === 'member-directory'"
              @click.stop="toggleMenu('member-directory')"
            >
              Member Directory
              <svg
                class="h-2.5 w-2.5 transition-transform"
                :class="{ 'rotate-180': openMenu === 'member-directory' }"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
            </button>
            <div
              v-if="openMenu === 'member-directory'"
              class="absolute top-full left-0 mt-1 min-w-[12rem] bg-white border border-gray-200 rounded-lg shadow-lg p-1.5 z-20"
            >
              <RouterLink
                to="/directory"
                class="block px-2.5 py-2 rounded-md text-sm font-medium text-sigmachi-navy hover:bg-gray-100"
                active-class="bg-indigo-50 font-bold"
                @click="openMenu = null"
              >
                Directory
              </RouterLink>
              <RouterLink
                to="/my-era"
                class="block px-2.5 py-2 rounded-md text-sm font-medium text-sigmachi-navy hover:bg-gray-100"
                active-class="bg-indigo-50 font-bold"
                @click="openMenu = null"
              >
                My Era
              </RouterLink>
              <RouterLink
                v-if="familyTreeEnabled"
                to="/family-tree"
                class="block px-2.5 py-2 rounded-md text-sm font-medium text-sigmachi-navy hover:bg-gray-100"
                active-class="bg-indigo-50 font-bold"
                @click="openMenu = null"
              >
                Family Tree <span class="text-xs opacity-75">(beta)</span>
              </RouterLink>
              <RouterLink
                to="/officers"
                class="block px-2.5 py-2 rounded-md text-sm font-medium text-sigmachi-navy hover:bg-gray-100"
                active-class="bg-indigo-50 font-bold"
                @click="openMenu = null"
              >
                Officers
              </RouterLink>
            </div>
          </div>
        </div>

        <!-- Right side: account dropdown -->
        <div class="hidden min-[860px]:flex min-[860px]:items-center flex-shrink-0">
          <template v-if="authStore.isAuthenticated">
            <div class="relative">
              <button
                type="button"
                class="text-gray-300 hover:bg-blue-700 hover:text-white px-3 py-2 rounded-md text-sm inline-flex items-center gap-1"
                :class="{ 'bg-blue-700 text-white': openMenu === 'account' }"
                :aria-expanded="openMenu === 'account'"
                @click.stop="toggleMenu('account')"
              >
                {{ authStore.user?.email }}
                <svg
                  class="h-2.5 w-2.5 transition-transform"
                  :class="{ 'rotate-180': openMenu === 'account' }"
                  viewBox="0 0 24 24"
                  fill="none"
                  aria-hidden="true"
                >
                  <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>
              <div
                v-if="openMenu === 'account'"
                class="absolute top-full right-0 mt-1 min-w-[10rem] bg-white border border-gray-200 rounded-lg shadow-lg p-1.5 z-20"
              >
                <RouterLink
                  to="/profile"
                  class="block px-2.5 py-2 rounded-md text-sm font-medium text-sigmachi-navy hover:bg-gray-100"
                  active-class="bg-indigo-50 font-bold"
                  @click="openMenu = null"
                >
                  My Profile
                </RouterLink>
                <RouterLink
                  to="/giving?utm_source=in-app-nav"
                  class="block px-2.5 py-2 rounded-md text-sm font-medium text-sigmachi-navy hover:bg-gray-100"
                  active-class="bg-indigo-50 font-bold"
                  @click="openMenu = null"
                >
                  1855 Club Giving
                </RouterLink>
                <template v-if="authStore.isAdmin">
                  <RouterLink
                    to="/admin"
                    class="block px-2.5 py-2 rounded-md text-sm font-medium text-sigmachi-navy hover:bg-gray-100"
                    @click="openMenu = null"
                  >
                    Admin
                  </RouterLink>
                  <RouterLink
                    to="/admin/guide"
                    class="block px-2.5 py-2 rounded-md text-sm font-medium text-sigmachi-navy hover:bg-gray-100"
                    @click="openMenu = null"
                  >
                    Admin Help
                  </RouterLink>
                </template>
                <button
                  type="button"
                  class="block w-full text-left px-2.5 py-2 rounded-md text-sm font-medium text-sigmachi-navy hover:bg-gray-100"
                  @click="handleLogout"
                >
                  Sign Out
                </button>
              </div>
            </div>
          </template>

          <template v-else>
            <RouterLink
              to="/login"
              class="text-white hover:bg-blue-700 px-3 py-2 rounded-md text-sm font-medium"
            >
              Sign In
            </RouterLink>
          </template>
        </div>

        <!-- Mobile menu button -->
        <div class="flex items-center min-[860px]:hidden">
          <button
            type="button"
            aria-label="Toggle menu"
            @click="isMobileMenuOpen = !isMobileMenuOpen"
            class="text-white hover:bg-blue-700 p-2 rounded-md"
          >
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                v-if="!isMobileMenuOpen"
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 6h16M4 12h16M4 18h16"
              />
              <path
                v-else
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile menu -->
    <div v-show="isMobileMenuOpen" class="min-[860px]:hidden">
      <div class="pt-2 pb-3 space-y-1" v-if="authStore.isAuthenticated">
        <a
          :href="MARKETING_HOME"
          class="text-white hover:bg-blue-700 block px-3 py-2 text-base font-medium"
        >
          Home
        </a>

        <!-- House Corp accordion -->
        <button
          type="button"
          class="text-white hover:bg-blue-700 flex items-center justify-between w-full px-3 py-2 text-base font-medium"
          @click="mobileHouseCorpOpen = !mobileHouseCorpOpen"
        >
          House Corp
          <svg
            class="h-2.5 w-2.5 transition-transform"
            :class="{ 'rotate-180': mobileHouseCorpOpen }"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <div v-show="mobileHouseCorpOpen" class="pl-6 space-y-1">
          <a
            :href="HOUSE_CORP_URL"
            class="text-white hover:bg-blue-700 block px-3 py-2 text-sm"
          >
            House Corp Overview
          </a>
          <a
            :href="TENANT_RESOURCES_URL"
            class="text-white hover:bg-blue-700 block px-3 py-2 text-sm"
          >
            Tenant Resources
          </a>
        </div>

        <a
          :href="NEWS_URL"
          class="text-white hover:bg-blue-700 block px-3 py-2 text-base font-medium"
        >
          News
        </a>

        <a
          :href="CLUB_1855_URL"
          class="text-white hover:bg-blue-700 block px-3 py-2 text-base font-medium"
        >
          1855 Club
        </a>

        <!-- Member Directory accordion -->
        <button
          type="button"
          class="text-white hover:bg-blue-700 flex items-center justify-between w-full px-3 py-2 text-base font-medium"
          @click="mobileMemberDirOpen = !mobileMemberDirOpen"
        >
          Member Directory
          <svg
            class="h-2.5 w-2.5 transition-transform"
            :class="{ 'rotate-180': mobileMemberDirOpen }"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <path d="M6 9l6 6 6-6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <div v-show="mobileMemberDirOpen" class="pl-6 space-y-1">
          <RouterLink
            to="/directory"
            class="text-white hover:bg-blue-700 block px-3 py-2 text-sm"
            @click="closeMobileMenu"
          >
            Directory
          </RouterLink>
          <RouterLink
            to="/my-era"
            class="text-white hover:bg-blue-700 block px-3 py-2 text-sm"
            @click="closeMobileMenu"
          >
            My Era
          </RouterLink>
          <RouterLink
            v-if="familyTreeEnabled"
            to="/family-tree"
            class="text-white hover:bg-blue-700 block px-3 py-2 text-sm"
            @click="closeMobileMenu"
          >
            Family Tree <span class="text-xs opacity-75">(beta)</span>
          </RouterLink>
          <RouterLink
            to="/officers"
            class="text-white hover:bg-blue-700 block px-3 py-2 text-sm"
            @click="closeMobileMenu"
          >
            Officers
          </RouterLink>
        </div>

        <div class="border-t border-blue-700 pt-2 mt-2">
          <span class="text-gray-300 block px-3 py-2 text-sm">
            {{ authStore.user?.email }}
          </span>
          <RouterLink
            to="/profile"
            class="text-white hover:bg-blue-700 block px-3 py-2 text-base font-medium"
            @click="closeMobileMenu"
          >
            My Profile
          </RouterLink>
          <RouterLink
            to="/giving?utm_source=in-app-nav"
            class="text-white hover:bg-blue-700 block px-3 py-2 text-base font-medium"
            @click="closeMobileMenu"
          >
            1855 Club Giving
          </RouterLink>
          <template v-if="authStore.isAdmin">
            <RouterLink
              to="/admin"
              class="text-white hover:bg-blue-700 block px-3 py-2 text-base font-medium"
              @click="closeMobileMenu"
            >
              Admin
            </RouterLink>
            <RouterLink
              to="/admin/guide"
              class="text-white hover:bg-blue-700 block px-3 py-2 text-base font-medium"
              @click="closeMobileMenu"
            >
              Admin Help
            </RouterLink>
          </template>
          <button
            type="button"
            @click="handleLogout"
            class="text-white hover:bg-blue-700 block w-full text-left px-3 py-2 text-base font-medium"
          >
            Sign Out
          </button>
        </div>
      </div>

      <div v-else class="pt-2 pb-3 space-y-1">
        <RouterLink
          to="/login"
          class="text-white hover:bg-blue-700 block px-3 py-2 text-base font-medium"
          @click="closeMobileMenu"
        >
          Sign In
        </RouterLink>
      </div>
    </div>
  </nav>
</template>
